/**
 * Registers the custom Python-like language, theme, and editor configuration for Monaco Editor.
 *
 * @param {object} monaco - Monaco instance from the editor
 * @param {object} colors - Monaco theme colors from MUI theme (optional)
 *
 * What this does:
 * - Registers a new language 'custom-python' if not already present.
 * - Sets up Monarch tokenizer rules for formula syntax, including:
 *   - Highlighting entire {{...}} blocks as a single token (formula-block)
 *   - Python-like keywords, numbers, strings, variables, etc.
 * - Configures editor behavior:
 *   - Disables bracket colorization for curly braces to ensure custom color for {{...}} blocks
 *   - Keeps auto-closing and surrounding pairs for all brackets, including {{...}}
 *   - Enables line comments with '#'
 * - Defines a custom theme for the editor, including colors for each token type.
 *
 * To extend: You can add more keywords, adjust regexes, or change the theme colors as needed.
 *
 * Note: Do NOT add curly braces to the brackets array, or Monaco's bracket colorizer will override custom color for {{...}} blocks.
 */
export const registerCustomPython = (
  monaco,
  { tokenColors = {}, colors = {} } = {}
) => {
  // Register language only if not already present
  if (
    !monaco?.languages
      ?.getLanguages()
      .some((lang) => lang.id === 'custom-python')
  ) {
    monaco.languages.register({ id: 'custom-python' });
  }
  monaco.languages.setMonarchTokensProvider('custom-python', {
    // Monarch tokenizer for custom formula syntax
    tokenizer: {
      root: [
        // Highlight entire {{...}} block (multiline, non-greedy, highest priority)
        [/\{\{[^{}]*(?:\{[^{}]*\}[^{}]*)*\}\}/, 'formula-block'],
        [/#[^\n]*/, 'comment'],
        [/"[^"]*"/, 'string'],
        [/'[^']*'/, 'string'],
        [
          /\b(def|return|if|else|for|while|in|and|or|not|True|False|None)\b/,
          'keyword',
        ],
        [/\b\d+(\.\d+)?\b/, 'number'],
        [/attributes\.[\w\s]+/, 'variable'],
        [/\b\w+\b/, 'identifier'],
      ],
    },
  });
  monaco.languages.setLanguageConfiguration('custom-python', {
    comments: {
      lineComment: '#',
    },
    // IMPORTANT: Do NOT include curly braces in brackets array to prevent Monaco's bracket colorizer from overriding custom {{...}} color
    brackets: [
      ['[', ']'],
      ['(', ')'],
    ],
    autoClosingPairs: [
      { open: '{{', close: '}}' },
      { open: '{', close: '}' },
      { open: '[', close: ']' },
      { open: '(', close: ')' },
      { open: '"', close: '"', notIn: ['string'] },
      { open: "'", close: "'", notIn: ['string'] },
    ],
    surroundingPairs: [
      { open: '{{', close: '}}' },
      { open: '{', close: '}' },
      { open: '[', close: ']' },
      { open: '(', close: ')' },
      { open: '"', close: '"' },
      { open: "'", close: "'" },
    ],
    folding: {
      offSide: true,
    },
  });
  monaco.editor.defineTheme('customPythonTheme', {
    base: 'vs',
    inherit: true,
    rules: [
      {
        token: 'formula-block',
        foreground: tokenColors['formula-block'],
        fontStyle: 'bold',
      },
      {
        token: 'comment',
        foreground: tokenColors['comment'],
        fontStyle: 'italic',
      },
      { token: 'string', foreground: tokenColors['string'] },
      {
        token: 'keyword',
        foreground: tokenColors['keyword'],
        fontStyle: 'bold',
      },
      { token: 'number', foreground: tokenColors['number'] },
      { token: 'variable', foreground: tokenColors['variable'] },
      {
        token: 'identifier',
        foreground: tokenColors['identifier'],
      },
    ],
    colors: colors,
  });
};
